import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Pause, ShieldCheck, Volume2 } from 'lucide-react';

const STARTERS = ['What needs me today?', 'Which files are stuck?', 'How are we tracking against target?', 'Who should I call first?'];

const Bubble = ({ m, chat, onAction }) => {
  const me = m.role === 'user';
  return (
    <div className={`flex ${me ? 'justify-end' : 'justify-start'}`} style={{ animation: 'jvIn .3s both' }}>
      <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${me ? 'rounded-br-md bg-[#0E1B4D]' : m.blocked ? 'border border-amber-300 bg-amber-50 text-amber-900' : m.error ? 'border border-rose-200 bg-rose-50 text-rose-800' : 'rounded-bl-md border border-[var(--line)] bg-[var(--surface-2,#FAFBFD)] text-[var(--ink)]'}`}
        style={me ? { color: '#fff' } : undefined} data-testid={me ? 'jarvis-chat-user-msg' : 'jarvis-chat-reply'}>
        {m.pending && !m.text ? <span className="inline-flex gap-1 py-1" aria-label="Jarvis is thinking">{[0, 1, 2].map((i) => <i key={i} className="h-1.5 w-1.5 animate-bounce rounded-full bg-[var(--muted)]" style={{ animationDelay: `${i * 0.15}s` }} />)}</span>
          : <span className="whitespace-pre-wrap">{m.text}</span>}
        {!me && !m.pending && !m.blocked && !m.error && m.text && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {(m.actions || []).filter((a) => a.insight).map((a) => (
              <button key={a.insight.id} type="button" onClick={() => onAction(a)} data-testid="jarvis-chat-action-chip"
                className="inline-flex items-center gap-1.5 rounded-xl border border-[#2347FF]/30 bg-[#ECF0FF] px-2.5 py-1 text-[11px] font-semibold text-[#1E3BD6] transition-colors hover:bg-[#2347FF] hover:text-white">
                <ShieldCheck className="h-3.5 w-3.5" />Review & approve: {a.insight.client || a.insight.title.slice(0, 28)}
              </button>
            ))}
            <button type="button" onClick={() => (chat.playing === m.id ? chat.stop() : chat.speak(m))} className="ml-auto rounded-md p-1 text-[var(--faint)] hover:bg-[var(--hover)]" title="Play reply" data-testid="jarvis-chat-play">
              {chat.playing === m.id ? <Pause className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export const Messages = ({ chat, onAction }) => {
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [chat.msgs]);
  const st = chat.status;
  return (
    <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="jarvis-chat-message-list">
      {st?.chat_mode === 'blocked' && (
        <div className="space-y-1.5 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="jarvis-chat-blocked-notice">
          <b>Chat is switched off for data protection.</b> An administrator can allow it in Security Centre → Data transfers.
          {st.can_configure && <div><Link to="/security" className="font-semibold underline">Open Security Centre</Link></div>}
        </div>
      )}
      {!chat.msgs.length && st?.chat_mode !== 'blocked' && (
        <div className="space-y-3 pt-2">
          <p className="text-sm text-[var(--muted)]">Ask about your pipeline, stuck files, targets or any client. Type or tap the mic.</p>
          <div className="flex flex-wrap gap-2">{STARTERS.map((s) => (
            <button key={s} type="button" onClick={() => chat.send(s)} className="rounded-full border border-[var(--line)] bg-[var(--surface)] px-3 py-1.5 text-xs font-semibold text-[var(--ink)] transition-colors hover:border-[#2347FF] hover:text-[#2347FF]" data-testid="jarvis-chat-starter-prompt">{s}</button>
          ))}</div>
        </div>
      )}
      {chat.msgs.map((m) => <Bubble key={m.id} m={m} chat={chat} onAction={onAction} />)}
      <div ref={end} />
    </div>
  );
};
